import React from 'react';
import { 
  Cloud, 
  Settings, 
  Users, 
  ShieldCheck, 
  Activity, 
  Code, 
  Database, 
  Globe, 
  Server,
  Terminal,
  Cpu,
  Workflow
} from 'lucide-react';
import './CloudServices.css';
import Hero from '../../components/Hero/Hero';
import FAQ from '../../components/FAQ/FAQ';
import CTASection from '../../components/CTA/CTA';
import Footer from '../../components/Footer/Footer';

const services = [
  {
    title: 'Managed Cloud Operations',
    description: 'We handle the complexity of your cloud infrastructure, providing 24/7 monitoring, incident management, and proactive patching.',
    features: [
      '24/7 Real-time monitoring and alerting',
      'Automated backup and disaster recovery',
      'Continuous security patching and updates'
    ],
    image: '/images/cloud-services/managed-ops.jpg'
  },
  {
    title: 'Cloud Integration Services',
    description: 'Connect your disparate systems with high-performance APIs and event-driven architectures for seamless data flow.',
    features: [
      'Hybrid and Multi-cloud connectivity',
      'Serverless integration patterns',
      'Message queue and event bus implementation'
    ],
    image: '/images/cloud-services/integration.jpg'
  },
  {
    title: 'Professional Cloud Support',
    description: 'Expert guidance on architectural decisions, compliance audits, and strategic cloud roadmap development.',
    features: [
      'Architectural review and optimization',
      'Compliance and regulatory alignment',
      'Dedicated cloud engineering support'
    ],
    image: '/images/cloud-services/support.jpg'
  }
];

const capabilities = [
  {
    icon: <Settings size={24} />,
    title: 'Infrastructure as Code',
    text: 'Standardize your environments with Terraform and CloudFormation for repeatable, error-free deployments.'
  },
  {
    icon: <Workflow size={24} />,
    title: 'DevOps Lifecycle',
    text: 'Accelerate delivery with automated CI/CD pipelines that bridge the gap between development and operations.'
  },
  {
    icon: <Cpu size={24} />,
    title: 'Resource Orchestration',
    text: 'Efficiently manage containers and microservices with Kubernetes and managed orchestration platforms.'
  },
  {
    icon: <Terminal size={24} />,
    title: 'Cloud Automation',
    text: 'Eliminate manual tasks with custom scripts and automated workflows that improve operational velocity.'
  }
];

const faqItems = [
  {
    question: 'How do managed cloud services benefit my business?',
    answer: 'Managed services allow your internal teams to focus on core business innovation while we handle the heavy lifting of infrastructure maintenance, security, and availability.'
  },
  {
    question: 'Do you support multi-cloud environments?',
    answer: 'Absolutely. We specialize in building and managing systems across AWS, Azure, and GCP, providing a unified management layer for your entire cloud portfolio.'
  },
  {
    question: 'What is your response time for critical issues?',
    answer: 'We offer tiered SLA-backed support with response times as fast as 15 minutes for critical production incidents, supported by our global 24/7 engineering team.'
  }
];

export default function CloudServicesPage() {
  return (
    <article className="cloud-services-page">
      <Hero 
        eyebrow="Managed Solutions"
        title="Comprehensive Cloud Operations."
        subtitle="Scale your business with expert managed services, seamless integrations, and 24/7 engineering support designed for the modern enterprise."
        stats={[
          { label: 'Uptime', value: '99.99%' },
          { label: 'Support', value: '24/7/365' }
        ]}
      />

      <section className="section section-capabilities">
        <div className="section-heading">
          <span className="eyebrow">Cloud Mastery</span>
          <h2>Operational excellence, simplified.</h2>
          <p>We provide the technical expertise and operational rigor needed to keep your cloud platform performing at its peak.</p>
        </div>
        <div className="capabilities-grid">
          {capabilities.map((cap, i) => (
            <div key={i} className="capability-card">
              <div className="cap-icon">{cap.icon}</div>
              <h3>{cap.title}</h3>
              <p>{cap.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section section-solutions">
        <div className="solutions-container">
          {services.map((service, index) => (
            <div key={index} className={`solution-row ${index % 2 !== 0 ? 'reverse' : ''}`}>
              <div className="solution-copy">
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <ul className="feature-list">
                  {service.features.map((f, fi) => (
                    <li key={fi}><ShieldCheck size={18} /> {f}</li>
                  ))}
                </ul>
              </div>
              <div className="solution-media">
                <img src={service.image} alt={service.title} />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="section section-metrics">
        <div className="metrics-grid">
          <div className="metric-item">
            <strong>15m</strong>
            <span>Avg. Response Time</span>
          </div>
          <div className="metric-item">
            <strong>99.9%</strong>
            <span>Platform Stability</span>
          </div>
          <div className="metric-item">
            <strong>Zero</strong>
            <span>Unplanned Downtime</span>
          </div>
          <div className="metric-item">
            <strong>100%</strong>
            <span>Compliance Score</span>
          </div>
        </div>
      </section>

      <section className="section section-faq">
        <div className="section-heading">
          <span className="eyebrow">Service Insight</span>
          <h2>Operational Questions.</h2>
        </div>
        <FAQ items={faqItems} />
      </section>

      <CTASection />
      
      <Footer />
    </article>
  );
}
